"use client";
import React, { useRef, useEffect, useState } from "react";
import { motion } from "motion/react";

interface TextHoverEffectSEOProps {
  text: string;
  duration?: number;
  theme?: "light" | "dark";
}

export const TextHoverEffectSEO = ({ text, duration, theme = "dark" }: TextHoverEffectSEOProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const [maskPosition, setMaskPosition] = useState({ cx: "50%", cy: "50%" });

  useEffect(() => {
    if (svgRef.current && cursor.x !== null && cursor.y !== null) {
      const svgRect = svgRef.current.getBoundingClientRect();
      const cxPercentage = ((cursor.x - svgRect.left) / svgRect.width) * 100;
      const cyPercentage = ((cursor.y - svgRect.top) / svgRect.height) * 100;
      setMaskPosition({
        cx: `${cxPercentage}%`,
        cy: `${cyPercentage}%`,
      });
    }
  }, [cursor]);

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      viewBox="0 0 500 120"
      xmlns="http://www.w3.org/2000/svg"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseMove={(e) => setCursor({ x: e.clientX, y: e.clientY })}
      className="select-none">
      <defs>
        <linearGradient
          id="textGradientSEO"
          gradientUnits="userSpaceOnUse"
          cx="50%"
          cy="50%"
          r="25%">
          {hovered && (
            <>
              <stop offset="0%" stopColor={theme === "light" ? "#082f49" : "#fff7ed"} />
              <stop offset="18%" stopColor={theme === "light" ? "#67e8f9" : "#fde68a"} />
              <stop offset="38%" stopColor={theme === "light" ? "#38bdf8" : "#facc15"} />
              <stop offset="62%" stopColor={theme === "light" ? "#14b8a6" : "#fb923c"} />
              <stop offset="82%" stopColor={theme === "light" ? "#0f766e" : "#f97316"} />
              <stop offset="100%" stopColor={theme === "light" ? "#0ea5e9" : "#f59e0b"} />
            </>
          )}
        </linearGradient>

        <motion.radialGradient
          id="revealMaskSEO"
          gradientUnits="userSpaceOnUse"
          r="20%"
          initial={{ cx: "50%", cy: "50%" }}
          animate={maskPosition}
          transition={{ duration: duration ?? 0, ease: "easeOut" }}>
          <stop offset="0%" stopColor="white" />
          <stop offset="100%" stopColor="black" />
        </motion.radialGradient>
        <mask id="textMaskSEO">
          <rect x="0" y="0" width="100%" height="100%" fill="url(#revealMaskSEO)" />
        </mask>
      </defs>
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        strokeWidth="1.5"
        className="fill-transparent font-[Raleway] text-7xl font-bold"
        stroke={theme === "light" ? "rgba(8,47,73,0.3)" : "rgba(255,237,213,0.34)"}
        style={{ opacity: hovered ? 0.7 : 0 }}>
        {text}
      </text>
      <motion.text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        strokeWidth="1.5"
        className="fill-transparent font-[Raleway] text-7xl font-bold"
        stroke={theme === "light" ? "rgba(20,184,166,0.55)" : "rgba(251,146,60,0.5)"}
        initial={{ strokeDashoffset: 1000, strokeDasharray: 1000 }}
        animate={{
          strokeDashoffset: 0,
          strokeDasharray: 1000,
        }}
        transition={{
          duration: 4,
          ease: "easeInOut",
        }}>
        {text}
      </motion.text>
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        stroke="url(#textGradientSEO)"
        strokeWidth="1.5"
        mask="url(#textMaskSEO)"
        className="fill-transparent font-[Raleway] text-7xl font-bold">
        {text}
      </text>
    </svg>
  );
}; 